import React, { SyntheticEvent, useEffect, useRef, useState } from "react";
import { useRouter } from "next/router";
import { track } from "@amplitude/analytics-browser";
import * as Yup from "yup";
import { Formik, FormikHelpers } from "formik";
import {
  confirmResetPassword,
  confirmSignUp,
  resendSignUpCode,
  resetPassword,
  signIn,
  signOut,
  signUp,
} from "aws-amplify/auth";

import Checkbox from "../common/Checkbox";
import FormValidationError from "../common/FormValidationError";
import Button from "../common/Button";
import Icon from "../common/Icon";
import FormikFormLine from "../formik-form-components/FormikFormLine";
import FormikPasswordField from "../formik-form-components/FormikPasswordField";

import {
  AuthInputValue,
  AuthMode,
  CHECK_EMAIL_FOR_CONFIRMATION_CODE,
  CONFIRM,
  CONFIRM_EMAIL_MODE,
  FORGOT_PASSWORD,
  GO_TO_MY_ACCOUNT,
  PASSKEY_MODE,
  PHONE_NUMBER,
  RESET_PASSWORD,
  SIGN_IN_MODE,
  SIGN_UP_MODE,
  SUCCESS,
} from "./constants";
import useFetchAndTrace from "../../utils/hooks/fetchWithTrace";
import {
  ACCOUNT_WITH_EMAIL_ALREADY_EXISTS,
  ATTEMPT_LIMIT_EXCEEDED,
  CODE_MISMATCH_EXCEPTION,
  INCORRECT_USERNAME_OR_PASSWORD,
  INVALID_CONFIRMATION_CODE,
  INVALID_PARAMETER_EXCEPTION,
  INVALID_PASSWORD_EXCEPTION,
  LIMIT_EXCEEDED_EXCEPTION,
  NO_ACCOUNT_WITH_THIS_EMAIL,
  NOT_AUTHORIZED_EXCEPTION,
  PASSKEY_AUTHENTICATION_CANCELED,
  PASSWORD_MUST_BE_AT_LEAST_8_CHARACTERS,
  SOMETHING_WENT_WRONG_PLEASE_TRY_AGAIN_LATER,
  UNCONFIRMED_EMAIL,
  USER_ALREADY_AUTHENTICATED_EXCEPTION,
  USER_NOT_CONFIRMED_EXCEPTION,
  USER_NOT_FOUND_EXCEPTION,
  USERNAME_EXISTS_EXCEPTION,
} from "../../constants/errors";
import { formatPhoneNumberInput } from "../../utils/valueFormatting";
import settings from "../../constants/settings";
import {
  CREATE_ACCOUNT_SOURCE,
  MARKETING_SIGN_UP_TEXT,
} from "../../constants/marketing";
import {
  FORGOT_PASSWORD as FORGOT_PASSWORD_LINK,
  MY_ACCOUNT,
  PRIVACY,
} from "../../constants/routes";
import { OPTIONAL, REQUIRED } from "../../constants/Text";
import { FieldTypes } from "../formik-form-components/FormikFormLine/constants";
import { ButtonTypes } from "../common/Button/constants";
import {
  EMAIL_SCHEMA,
  firstNameSchema,
  getNewPasswordSchema,
  getPhoneNumberSchema,
  lastNameSchema,
  PASSWORD_SCHEMA,
} from "../../utils/forms";
import * as styledElements from "./AuthenticationStyles";
import {
  errorToString,
  tracer,
  useSendError,
} from "../../utils/observability/logging";
import WhyRegister from "./WhyRegister";
import { useLoadAuth } from "../../utils/hooks/auth";
import { Span } from "@opentelemetry/api";

interface FormValues {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: string;
  organization?: string;
  password: string;
  confirmationCode: string;
}

export const confirmationCodeSchema = Yup.string()
  .test(
    "Digits only",
    "The field should have digits only",
    (value) => value !== undefined && /^\d+$/.test(value),
  )
  .required("Confirmation code is required");

const initialValues = {
  firstName: "",
  lastName: "",
  phoneNumber: "",
  organization: "",
  email: "",
  password: "",
  confirmationCode: "",
};

const Authentication = ({
  initialAuthMode,
  allowPasskeys,
}: {
  initialAuthMode: AuthMode;
  allowPasskeys?: boolean;
}) => {
  const loadAuth = useLoadAuth();
  const emailInputRef = useRef<HTMLInputElement>(null);
  const fetchAndTrace = useFetchAndTrace();
  const router = useRouter();
  const sendError = useSendError();
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [loading, setLoading] = useState(false);
  const [authMode, setAuthMode] = useState<AuthMode>(initialAuthMode);

  const toggleMarketingOptIn = () => setMarketingOptIn(!marketingOptIn);

  const handleGenericError = (error: Error, activeSpan?: Span) => {
    setLoading(false);
    sendError(error, { activeSpan });
    setError(SOMETHING_WENT_WRONG_PLEASE_TRY_AGAIN_LATER);
  };

  const handleStartOfRequest = () => {
    setLoading(true);
    setError("");
    setInfo("");
  };

  useEffect(() => {
    if (authMode == PASSKEY_MODE && emailInputRef.current !== null) {
      emailInputRef.current.focus();
    }
  }, [authMode]);

  const chooseSignInMethod = async (values: FormValues) => {
    const lowercaseEmail = values.email.toLowerCase();
    if (authMode == SIGN_IN_MODE) {
      return await signIn({
        username: lowercaseEmail,
        password: values.password,
      });
    } else if (authMode == PASSKEY_MODE) {
      return await signIn({
        username: lowercaseEmail,
        options: {
          authFlowType: "USER_AUTH",
          preferredChallenge: "WEB_AUTHN",
        },
      });
    } else {
      throw Error(`Unhandled auth mode ${authMode}`);
    }
  };

  const handleSignIn = async (values: FormValues) => {
    tracer.startActiveSpan("handleSignIn", async (span: Span) => {
      try {
        const signInOutput = await chooseSignInMethod(values);
        if (signInOutput.nextStep.signInStep == "CONFIRM_SIGN_UP") {
          setAuthMode(CONFIRM_EMAIL_MODE);
        } else {
          track("Signed in");
          await loadAuth();
        }
      } catch (error) {
        span.setAttribute("signInError", errorToString(error));
        if (error instanceof Error) {
          if (error.name == USER_NOT_CONFIRMED_EXCEPTION) {
            handleSetAuthMode(CONFIRM_EMAIL_MODE);
          } else if (
            error.name == NOT_AUTHORIZED_EXCEPTION ||
            error.name == USER_NOT_FOUND_EXCEPTION
          ) {
            setError(INCORRECT_USERNAME_OR_PASSWORD);
          } else if (error.name == USER_ALREADY_AUTHENTICATED_EXCEPTION) {
            track("Already signed in");
            await loadAuth();
          } else if (error.name != PASSKEY_AUTHENTICATION_CANCELED) {
            handleGenericError(error, span);
          }
        }
      } finally {
        span.end();
      }
    });
    setLoading(false);
  };

  const handleSignUp = async (values: FormValues) => {
    tracer.startActiveSpan("handleSignUp", async (span: Span) => {
      try {
        const lowercaseEmail = values.email.toLowerCase();
        handleStartOfRequest();
        if (marketingOptIn) {
          void fetchAndTrace(
            `${settings.api_url}/add-marketing-contact`,
            null,
            null,
            {
              body: JSON.stringify({
                email: lowercaseEmail,
                first_name: values.firstName,
                last_name: values.lastName,
                source: CREATE_ACCOUNT_SOURCE,
              }),
              method: "PUT",
            },
          );
        }
        await signUp({
          username: lowercaseEmail,
          password: values.password,
          options: {
            userAttributes: {
              given_name: values.firstName,
              family_name: values.lastName,
              "custom:organization": values.organization,
              phone_number: formatPhoneNumberInput(
                values.phoneNumber === undefined ? "" : values.phoneNumber,
              ),
            },
          },
        });
        setAuthMode(CONFIRM_EMAIL_MODE);
      } catch (error) {
        span.setAttribute("signUpError", errorToString(error));
        if (error instanceof Error) {
          if (error.name == USERNAME_EXISTS_EXCEPTION) {
            setError(ACCOUNT_WITH_EMAIL_ALREADY_EXISTS);
          } else {
            handleGenericError(error, span);
          }
        }
      } finally {
        span.end();
      }
    });
    setLoading(false);
  };

  const handleConfirmEmail = async (values: FormValues) => {
    tracer.startActiveSpan("handleConfirmEmail", async (span: Span) => {
      try {
        const lowercaseEmail = values.email.toLowerCase();
        handleStartOfRequest();
        await confirmSignUp({
          username: lowercaseEmail,
          confirmationCode: values.confirmationCode,
        });
        await signIn({ username: lowercaseEmail, password: values.password });
        await loadAuth();
        track("Account created", { "marketing-opt-in": marketingOptIn });
      } catch (error) {
        span.setAttribute("confirmEmailError", errorToString(error));
        if (error instanceof Error) {
          if (error.name == CODE_MISMATCH_EXCEPTION) {
            setError(INVALID_CONFIRMATION_CODE);
          } else if (error.name == NOT_AUTHORIZED_EXCEPTION) {
            // prevents unhelpful error message in the event that the user enters the wrong password
            // then a correct email confirmation code
            setAuthMode(SIGN_IN_MODE);
            setError(INCORRECT_USERNAME_OR_PASSWORD);
          } else {
            handleGenericError(error);
          }
        }
      } finally {
        span.end();
      }
    });
    setLoading(false);
  };

  const handleForgotPassword = async (
    values: FormValues,
    { resetForm }: FormikHelpers<FormValues>,
  ) => {
    tracer.startActiveSpan("handleForgotPassword", async (span: Span) => {
      try {
        const lowercaseEmail = values.email.toLowerCase();
        handleStartOfRequest();
        await resetPassword({ username: lowercaseEmail });
        handleSetAuthMode(RESET_PASSWORD);
        setInfo(CHECK_EMAIL_FOR_CONFIRMATION_CODE);
        resetForm({
          values: {
            ...initialValues,
            email: lowercaseEmail,
          },
        });
      } catch (error) {
        span.setAttribute("forgotPasswordError", errorToString(error));
        if (error instanceof Error) {
          if (error.name == USER_NOT_FOUND_EXCEPTION) {
            setError(NO_ACCOUNT_WITH_THIS_EMAIL);
          } else if (error.name == INVALID_PARAMETER_EXCEPTION) {
            setError(UNCONFIRMED_EMAIL);
          } else if (error.name == LIMIT_EXCEEDED_EXCEPTION) {
            setError(ATTEMPT_LIMIT_EXCEEDED);
          } else {
            handleGenericError(error, span);
          }
        }
      } finally {
        span.end();
      }
    });
    setLoading(false);
  };

  const handleResetPassword = async (values: FormValues) => {
    tracer.startActiveSpan("handleResetPassword", async (span: Span) => {
      try {
        const lowercaseEmail = values.email.toLowerCase();
        handleStartOfRequest();
        await confirmResetPassword({
          username: lowercaseEmail,
          confirmationCode: values.confirmationCode,
          newPassword: values.password,
        });
        await signIn({ username: lowercaseEmail, password: values.password });
        await signOut({ global: true });
        // wait 1 second because subsequent calls often fail if you don't
        await new Promise((resolve) => setTimeout(resolve, 1000));
        await signIn({ username: lowercaseEmail, password: values.password });
        await loadAuth();
        setAuthMode(SUCCESS);
        setInfo("Password reset! You are now logged in.");
      } catch (error) {
        span.setAttribute("resetPasswordError", errorToString(error));
        if (error instanceof Error) {
          if (error.name == CODE_MISMATCH_EXCEPTION) {
            setError(INVALID_CONFIRMATION_CODE);
          } else if (
            error.name == INVALID_PARAMETER_EXCEPTION ||
            error.name == INVALID_PASSWORD_EXCEPTION
          ) {
            setError(PASSWORD_MUST_BE_AT_LEAST_8_CHARACTERS);
          } else {
            handleGenericError(error, span);
          }
        }
      } finally {
        span.end();
      }
    });
    setLoading(false);
  };

  const handleSetAuthMode = (newAuthMode: AuthMode) => {
    if (newAuthMode == CONFIRM_EMAIL_MODE) {
      setInfo(CHECK_EMAIL_FOR_CONFIRMATION_CODE);
    }
    setLoading(false);
    setError("");
    setAuthMode(newAuthMode);
  };

  const goToMyAccount = (event: SyntheticEvent) => {
    event.preventDefault();
    void router.push(MY_ACCOUNT);
  };

  const getSubmitHandler = () => {
    if (authMode == SIGN_IN_MODE || authMode == PASSKEY_MODE) {
      return handleSignIn;
    } else if (authMode == SIGN_UP_MODE) {
      return handleSignUp;
    } else if (authMode == CONFIRM_EMAIL_MODE) {
      return handleConfirmEmail;
    } else if (authMode == FORGOT_PASSWORD) {
      return handleForgotPassword;
    } else {
      return handleResetPassword;
    }
  };

  const INPUT_DATA = {
    firstName: {
      type: FieldTypes.text,
      name: "firstName",
      label: "First name",
      placeholder: "Enter first name",
    },
    lastName: {
      type: FieldTypes.text,
      name: "lastName",
      label: "Last name",
      placeholder: "Enter last name",
    },
    email: {
      type: FieldTypes.email,
      name: "email",
      label: "Email",
      placeholder: "Enter email",
      ref: emailInputRef,
    },
    password: {
      type: FieldTypes.password,
      name: "password",
      label: "Password",
      placeholder: `${authMode == SIGN_IN_MODE ? "" : "Create "}Password`,
    },
    confirmationCode: {
      type: FieldTypes.text,
      name: "confirmationCode",
      label: "Confirmation code",
    },
    organization: {
      placeholder: "Enter organization or company",
      type: FieldTypes.text,
      name: "organization",
      label: "Organization",
      required: false,
      subLabel: OPTIONAL,
    },
    phoneNumber: {
      placeholder: "Enter phone number",
      type: FieldTypes.tel,
      name: "phoneNumber",
      label: PHONE_NUMBER,
      subLabel: `US Only - ${OPTIONAL}`,
      required: false,
    },
  };

  const getInputData = () => {
    const inputData: AuthInputValue[] = [];
    if (authMode == SIGN_UP_MODE) {
      inputData.push(INPUT_DATA.firstName);
      inputData.push(INPUT_DATA.lastName);
      inputData.push(INPUT_DATA.phoneNumber);
      inputData.push(INPUT_DATA.organization);
    }
    if (
      authMode == SIGN_IN_MODE ||
      authMode == SIGN_UP_MODE ||
      authMode == PASSKEY_MODE ||
      authMode == FORGOT_PASSWORD
    ) {
      inputData.push(INPUT_DATA.email);
    }
    if (authMode == SIGN_IN_MODE || authMode == SIGN_UP_MODE) {
      inputData.push(INPUT_DATA.password);
    }
    if (authMode == CONFIRM_EMAIL_MODE) {
      inputData.push(INPUT_DATA.confirmationCode);
    }
    if (authMode == RESET_PASSWORD) {
      inputData.push(INPUT_DATA.password);
      inputData.push(INPUT_DATA.confirmationCode);
    }
    return inputData;
  };

  const getSchema = () => {
    if (authMode == SIGN_UP_MODE) {
      return Yup.object().shape({
        firstName: firstNameSchema,
        lastName: lastNameSchema,
        phoneNumber: getPhoneNumberSchema(false),
        organization: Yup.string(),
        email: EMAIL_SCHEMA,
        password: getNewPasswordSchema(false),
      });
    } else if (authMode == SIGN_IN_MODE) {
      return Yup.object().shape({
        email: EMAIL_SCHEMA,
        password: PASSWORD_SCHEMA,
      });
    } else if (authMode == PASSKEY_MODE) {
      return Yup.object().shape({
        email: EMAIL_SCHEMA,
      });
    } else if (authMode == CONFIRM_EMAIL_MODE) {
      return Yup.object().shape({
        confirmationCode: confirmationCodeSchema,
      });
    } else if (authMode == FORGOT_PASSWORD) {
      return Yup.object().shape({
        email: EMAIL_SCHEMA,
      });
    } else {
      return Yup.object().shape({
        password: getNewPasswordSchema(false),
        confirmationCode: confirmationCodeSchema,
      });
    }
  };

  const getSubmitButtonText = () => {
    if (authMode == CONFIRM_EMAIL_MODE) {
      return CONFIRM;
    } else if (authMode == SIGN_UP_MODE) {
      return "Register";
    } else if (authMode == SIGN_IN_MODE || authMode == PASSKEY_MODE) {
      return "Sign in";
    } else if (authMode == FORGOT_PASSWORD) {
      return "Send reset code";
    } else if (authMode == RESET_PASSWORD) {
      return "Reset password";
    } else {
      return GO_TO_MY_ACCOUNT;
    }
  };

  const getIconPath = () => {
    if (authMode == SIGN_IN_MODE) {
      return "/icons/sign_in.png";
    } else if (authMode == SIGN_UP_MODE) {
      return "/icons/sign_up.png";
    }
    return null;
  };

  const iconPath = getIconPath();

  const header = (
    <>
      {iconPath && <Icon src={iconPath} alt={authMode} iconDimension={80} />}
      <styledElements.Header>{authMode}</styledElements.Header>
    </>
  );

  return (
    <styledElements.Container
      isSignUp={authMode == SIGN_UP_MODE}
      useSmallSize={
        authMode == FORGOT_PASSWORD ||
        authMode == RESET_PASSWORD ||
        authMode == SUCCESS
      }
    >
      {authMode == SIGN_UP_MODE ? (
        <styledElements.LeftColumn>
          {header}
          <WhyRegister />
        </styledElements.LeftColumn>
      ) : (
        <>{header}</>
      )}
      <Formik
        initialValues={initialValues as FormValues}
        validationSchema={getSchema()}
        onSubmit={getSubmitHandler()}
      >
        {({ handleSubmit, isValid, dirty, values, resetForm }) => {
          const resendLink = () => {
            try {
              handleStartOfRequest();
              void resendSignUpCode({
                username: values.email.toLowerCase(),
              }).then(() => setLoading(false));
              resetForm({
                values: {
                  ...values,
                  confirmationCode: initialValues.confirmationCode,
                },
              });
            } catch (error) {
              if (error instanceof Error) {
                handleGenericError(error);
              }
            }
          };

          const setAuthModeAndClearPassword = (authMode: AuthMode) => {
            resetForm({
              values: {
                ...values,
                password: initialValues.password,
              },
            });
            setAuthMode(authMode);
          };

          const footerLinks = (
            <>
              {authMode == SIGN_IN_MODE && (
                <styledElements.FooterLink
                  onClick={() => setAuthModeAndClearPassword(SIGN_UP_MODE)}
                >
                  Create account
                </styledElements.FooterLink>
              )}
              {authMode == SIGN_UP_MODE && (
                <styledElements.FooterLink
                  onClick={() => setAuthModeAndClearPassword(SIGN_IN_MODE)}
                >
                  Already have an account?
                </styledElements.FooterLink>
              )}
            </>
          );
          return (
            <styledElements.Form
              onSubmit={authMode == SUCCESS ? goToMyAccount : handleSubmit}
              isSignUp={authMode == SIGN_UP_MODE}
            >
              {getInputData().map((formLine, index) =>
                formLine.type == FieldTypes.password ? (
                  <FormikPasswordField
                    key={`${formLine.name}-${index}`}
                    placeholder={formLine.placeholder || REQUIRED}
                    name={formLine.name}
                    label={formLine.label}
                    ref={formLine.ref}
                  />
                ) : (
                  <FormikFormLine
                    key={`${formLine.name}-${index}`}
                    name={formLine.name}
                    labelComponent={
                      <styledElements.FormLabel>
                        {formLine.label}
                        {formLine.subLabel && (
                          <span> ({formLine.subLabel})</span>
                        )}
                      </styledElements.FormLabel>
                    }
                    type={formLine.type}
                    isRequired={formLine.required}
                    placeholder={formLine.placeholder}
                    disabled={loading}
                    ref={formLine.ref}
                  />
                ),
              )}
              {authMode == SIGN_UP_MODE && (
                <styledElements.MarketingCheckboxArea>
                  <div>
                    <Checkbox
                      checked={marketingOptIn}
                      onChange={toggleMarketingOptIn}
                    />
                  </div>
                  <label onClick={toggleMarketingOptIn}>
                    {MARKETING_SIGN_UP_TEXT}
                  </label>
                </styledElements.MarketingCheckboxArea>
              )}
              <styledElements.InfoContainer>
                {error && <FormValidationError validationMessage={error} />}
                {info && <>{info}</>}
              </styledElements.InfoContainer>
              <styledElements.ButtonContainer>
                <Button
                  type={ButtonTypes.SUBMIT}
                  disabled={loading || !(isValid && dirty)}
                  label={getSubmitButtonText()}
                  centerText
                  isLoading={loading}
                />
              </styledElements.ButtonContainer>
              <styledElements.FooterLinks>
                {(authMode == SIGN_IN_MODE || authMode == PASSKEY_MODE) && (
                  <styledElements.FooterLink
                    onClick={() => void router.push(FORGOT_PASSWORD_LINK)}
                  >
                    Forgot password?
                  </styledElements.FooterLink>
                )}
                {authMode == CONFIRM_EMAIL_MODE && (
                  <styledElements.FooterLink
                    onClick={() => !loading && resendLink()}
                  >
                    Resend code
                  </styledElements.FooterLink>
                )}
                {authMode == SIGN_IN_MODE && (
                  <>
                    <styledElements.VerticalLine />
                    {footerLinks}
                    {allowPasskeys && (
                      <>
                        <styledElements.VerticalLine />
                        <styledElements.FooterLink
                          onClick={() =>
                            setAuthModeAndClearPassword(PASSKEY_MODE)
                          }
                        >
                          Use passkey
                        </styledElements.FooterLink>
                      </>
                    )}
                  </>
                )}
                {authMode == PASSKEY_MODE && (
                  <>
                    {footerLinks}
                    <styledElements.VerticalLine />
                    <styledElements.FooterLink
                      onClick={() => setAuthModeAndClearPassword(SIGN_IN_MODE)}
                    >
                      Use password
                    </styledElements.FooterLink>
                  </>
                )}
                {authMode == SIGN_UP_MODE && (
                  <>
                    {footerLinks}
                    <styledElements.VerticalLine />
                    <styledElements.FooterLink
                      onClick={() => void router.push(PRIVACY)}
                    >
                      Privacy
                    </styledElements.FooterLink>
                  </>
                )}
              </styledElements.FooterLinks>
            </styledElements.Form>
          );
        }}
      </Formik>
    </styledElements.Container>
  );
};

export default Authentication;
